import React, { useEffect, useState } from "react";
import {
  FlightResultContent,
  selectChosenFlightsResults,
} from "../../searchFlightsSlice";
import { useAppSelector } from "../../../../app/hooks";
import { useTranslation } from "react-i18next";
import { ConfigSliceState, selectConfig } from "../../../config/ConfigSlice";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons/faExclamationTriangle";
import SearchFlightsResultSFO from "./SearchFlightsResultSFO";
import { faExclamationCircle } from "@fortawesome/free-solid-svg-icons/faExclamationCircle";
import { faArrowUp } from "@fortawesome/free-solid-svg-icons/faArrowUp";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons/faArrowDown";
import SearchFlightsResultsSort from "../SearchFlightsResultsSort";

function SearchFlightsResults({ layout }) {
  const DEFAULT_ITEMS = 9;
  const { t } = useTranslation();
  const tableField = [
    { name: t("HEADER_SORT_AIRLINE"), key: "airline" },
    { name: t("LABEL_AIRCRAFT"), key: "aircraft" },
    { name: t("LABEL_ORIGIN"), key: "depart" },
    { name: t("FLIGHT"), key: "operatedBy" },
    { name: t("LABEL_DESTINATION"), key: "arrival" },
    { name: t("DEPARTS"), key: "departureTimeAsString" },
    { name: t("ARRIVES"), key: "arrivalTimeAsString" },
    { name: t("HEADER_SORT_STOPS"), key: "stops" },
    { name: t("LABEL_TOTAL_FLIGHT_TIME"), key: "travelTime" },
  ];

  const { mapper, components }: ConfigSliceState = useAppSelector(selectConfig);
  const results: FlightResultContent = useAppSelector(
    selectChosenFlightsResults
  );
  const type = useAppSelector((state) => state.searchFlights.results.type);
  const [noOfElements, setnoOfElements] = useState(DEFAULT_ITEMS);
  const [sorting, setSorting] = useState({
    key: "departureTimeAsString",
    ascending: true,
  });
  const [flights, setFlights] = useState([]);
  const sliceFlights = flights.slice(0, noOfElements);
  const loadMore = () => {
    setnoOfElements(noOfElements + DEFAULT_ITEMS);
  };
  const loadLess = () => {
    setnoOfElements(DEFAULT_ITEMS);
  };

  const sortBy = (key, ascending) => {
    setSorting({ key: key, ascending: !ascending });
  };

  useEffect(() => {
    if (results != null) {
      const sortFlights = [...results?.filtered].sort((a, b) => {
        if (sorting.key === "departureTimeAsString") {
          const diff = a.departureDate - b.departureDate;
          if (diff === 0 && a.codeshareInd !== b.codeshareInd) {
            return a.codeshareInd ? 1 : -1;
          }
          return diff;
        } else if (sorting.key === "arrivalTimeAsString") {
          const aTime =
            a.connxLeg3ArrivalDate ||
            a.connxLeg2ArrivalDate ||
            a.connxLeg1ArrivalDate ||
            a.arrivalDate;
          const bTime =
            b.connxLeg3ArrivalDate ||
            b.connxLeg2ArrivalDate ||
            b.connxLeg1ArrivalDate ||
            b.arrivalDate;
          return aTime - bTime;
        } else {
          return a[sorting.key]
            .toString()
            .localeCompare(b[sorting.key].toString(), "en", {
              numeric: true,
            });
        }
      });
      if (sorting.key !== "depart" && sorting.key !== "arrival") {
        setFlights(sorting.ascending ? sortFlights : sortFlights.reverse());
      }
      setnoOfElements(noOfElements);
    }
  }, [results, sorting, noOfElements]);

  if (results && results.error) {
    return (
      <div className="notification is-danger m-1">
        <FontAwesomeIcon icon={faExclamationCircle} />
        &nbsp;
        {t("ERROR_CANNOT_RETRIEVE_RESULTS")}
      </div>
    );
  }

  if (results && !results.error && results.flights) {
    if (results.flights.length === 0) {
      if (type.startsWith("direct")) {
        return (
          <div className="notification is-warning m-1">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            &nbsp;
            {t("HEADER_NO_DIRECT_FLIGHTS")}
          </div>
        );
      } else {
        return (
          <div className="notification is-warning m-1">
            <FontAwesomeIcon icon={faExclamationTriangle} />
            &nbsp;
            {t("HEADER_NO_CONNECTING_FLIGHTS")}
          </div>
        );
      }
    } else {
      return (
        <>
          <SearchFlightsResultsSort processedRows={results.filtered}  />
          <div id="search-flights-results" className="flight-results" role="table">
            <div className="flight-results-header" role="rowgroup">
              <div className="columns is-mobile is-vcentered">
                <div className="column px-0">
                  <div
                    className="sfo-category columns is-mobile is-vcentered px-2"
                    role="row"
                  >
                    {tableField.map((field, i) => {
                      let className = "column table-field px-0";
                      switch (field.name) {
                        case t("FLIGHT"):
                          field.name += " #";
                          break;
                        case t("HEADER_SORT_AIRLINE"):
                          className += " airline-column";
                          break;
                        case t("HEADER_SORT_STOPS"):
                          className += " flight-stops";
                          break;
                        case t("LABEL_TOTAL_FLIGHT_TIME"):
                          className += " total-flight-time";
                          break;
                      }
                      if (field.key === "arrival" || field.key === "depart") {
                        return (
                          <div
                            className={className + " no-sorting"}
                            key={i}
                            role="columnheader"
                          >
                            {field.name}
                          </div>
                        );
                      } else {
                        return (
                          <div
                            className={className}
                            key={i}
                            onClick={() => sortBy(field.key, sorting.ascending)}
                            role="columnheader"
                          >
                            {field.name}
                            {field.key === sorting.key &&
                            field.key !== "arrival" &&
                            field.key !== "depart" ? (
                              sorting.ascending ? (
                                <FontAwesomeIcon icon={faArrowUp} />
                              ) : (
                                <FontAwesomeIcon icon={faArrowDown} />
                              )
                            ) : (
                              ""
                            )}
                          </div>
                        );
                      }
                    })}
                    <div className="column px-0" role="columnheader">
                      {t("LABEL_DETAILS")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {sliceFlights.map((result, i) => (
              <SearchFlightsResultSFO
                key={i}
                index={i}
                dateMin={results.dateMin}
                dateMax={results.dateMax}
                result={result}
                mapper={mapper}
                components={components}
                layout={layout}
              />
            ))}
          </div>
          <div className="column has-text-centered">
            <div>
              {`${t("SHOWING_LABEL")} ${
                results.filtered.length - noOfElements > 0
                  ? noOfElements
                  : results.filtered.length
              } ${t("OF_LABEL")} ${results.filtered.length}`}
            </div>
            <button
              className={
                noOfElements <= results.filtered.length
                  ? "show-result-count"
                  : "show-result-count hidden"
              }
              onClick={loadMore}
            >
              + {t("SEE_MORE_LABEL")}
            </button>
          </div>
        </>
      );
    }
  } else {
    return null;
  }
}

export default SearchFlightsResults;
